import React, { useEffect, useRef, useState } from "react";

/**
 * NumeriArabell — sezione statistiche in stile Arabell (dark + gold).
 * Contatori che si animano al primo scroll-in-view.
 * Coerente con il resto della home: font-display Cormorant, letter-spacing wide,
 * gold #c9a961 come accent, nessun elemento fuori dallo stile brand.
 */
const STATS = [
  { value: 5, suffix: "+", label: "Divisioni Operative", sub: "Auto · Aviation · Yacht · Real Estate · Wallet" },
  { value: 150, suffix: "+", label: "Veicoli Gestiti", sub: "Compravendita, noleggio & flotta" },
  { value: 20, suffix: "+", label: "Città Servite", sub: "Italia · Europa · Golfo Persico" },
  { value: 24, suffix: "/7", label: "Concierge Riservato", sub: "Consulenza dedicata su richiesta" },
];

const useInView = (opts = { threshold: 0.25 }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      opts,
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
};

const Counter = ({ end, suffix = "", duration = 1800 }) => {
  const [n, setN] = useState(0);
  const [ref, inView] = useInView();
  useEffect(() => {
    if (!inView) return;
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      // easeOutQuart per un effetto "arrival" luxury
      const eased = 1 - Math.pow(1 - p, 4);
      setN(Math.round(eased * end));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);
  return (
    <span ref={ref} className="tabular-nums">
      {n}{suffix}
    </span>
  );
};

const NumeriArabell = () => (
  <section
    className="bg-[#0a0a0a] py-16 md:py-24 border-t border-white/5"
    data-testid="home-numeri-arabell"
  >
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
      <p className="text-[10px] tracking-[0.5em] uppercase text-arabell-gold mb-3">
        Il Gruppo in numeri
      </p>
      <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white tracking-[0.05em] mb-3">
        Presenza concreta, servizio riservato.
      </h2>
      <p className="text-white/50 text-sm md:text-base max-w-2xl mx-auto mb-14 leading-relaxed">
        Un ecosistema unico di servizi premium — dalla mobilità al patrimonio —
        gestito da un team dedicato per una clientela selezionata.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-8">
        {STATS.map((s, i) => (
          <div
            key={s.label}
            className="border-l border-arabell-gold/25 pl-4 md:pl-6 text-left"
            data-testid={`numeri-stat-${i}`}
          >
            <div className="font-display text-4xl md:text-5xl lg:text-6xl text-arabell-gold tracking-tight mb-2 leading-none">
              <Counter end={s.value} suffix={s.suffix} />
            </div>
            <p className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-white/90 font-medium mb-2">
              {s.label}
            </p>
            <p className="text-[11px] text-white/45 leading-relaxed">
              {s.sub}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default NumeriArabell;
